import { PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { TariffGroupingDTO } from "../backend-types";
import { useConfirmationModal } from "../components/ConfirmationModal";
import TableMenu, { MenuAction } from "../components/MenuAction";
import SidebarPageLayout from "../components/SidebarPageLayout";
import { toastStore } from "../components/ToastContainer";
import { handleError } from "../errorHandler";
import { rootStore } from "../stores/RootStore";

const TariffGroupingPage: React.FC = observer(() => {
  const { tariffGroupingStore } = rootStore;
  const { showConfirmationModal, ConfirmationModalComponent } = useConfirmationModal();
  const [editingTariffGrouping, setEditingTariffGrouping] = useState<TariffGroupingDTO | null>(null);

  const [newTariffGrouping, setNewTariffGrouping] = useState<Omit<TariffGroupingDTO, "id">>({
    name: "",
    terms: [""],
  });

  useEffect(() => {
    tariffGroupingStore.refreshTariffGroupings();
  }, [tariffGroupingStore]);

  const handleAddTerm = () => {
    if (editingTariffGrouping) {
      setEditingTariffGrouping((prev) => ({
        ...prev!,
        terms: [...prev!.terms, ""],
      }));
    } else {
      setNewTariffGrouping((prev) => ({
        ...prev,
        terms: [...prev.terms, ""],
      }));
    }
  };

  const handleTermChange = (index: number, value: string) => {
    if (editingTariffGrouping) {
      setEditingTariffGrouping((prev) => ({
        ...prev!,
        terms: prev!.terms.map((term, i) => (i === index ? value : term)),
      }));
    } else {
      setNewTariffGrouping((prev) => ({
        ...prev,
        terms: prev.terms.map((term, i) => (i === index ? value : term)),
      }));
    }
  };

  const handleRemoveTerm = (index: number) => {
    if (editingTariffGrouping) {
      if (editingTariffGrouping.terms.length <= 1) return;

      setEditingTariffGrouping((prev) => ({
        ...prev!,
        terms: prev!.terms.filter((_, i) => i !== index),
      }));
    } else {
      if (newTariffGrouping.terms.length <= 1) return;

      setNewTariffGrouping((prev) => ({
        ...prev,
        terms: prev.terms.filter((_, i) => i !== index),
      }));
    }
  };

  const handleCreateTariffGrouping = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTariffGrouping.name || newTariffGrouping.terms.some((term) => !term.trim())) return;

    try {
      await tariffGroupingStore.createTariffGrouping({
        ...newTariffGrouping,
        terms: newTariffGrouping.terms.filter((term) => term.trim()),
      });
      toastStore.add({
        type: "success",
        caption: "Erfolg",
        message: "Tarif-Gruppierung wurde erfolgreich erstellt",
      });
      setNewTariffGrouping({
        name: "",
        terms: [""],
      });
    } catch (error) {
      const { type, message } = handleError(error, "Tarif-Gruppierung erstellen");
      toastStore.add({ type, caption: "Fehler", message });
    }
  };

  const handleDeleteTariffGrouping = async (id: string) => {
    const confirmed = await showConfirmationModal({
      title: "Tarif-Gruppierung löschen",
      text: "Möchten Sie diese Tarif-Gruppierung wirklich löschen?",
      confirmText: "Löschen",
      cancelText: "Abbrechen",
    });

    if (confirmed) {
      try {
        await tariffGroupingStore.deleteTariffGrouping(id);
        toastStore.add({
          type: "success",
          caption: "Erfolg",
          message: "Tarif-Gruppierung wurde erfolgreich gelöscht",
        });
      } catch (error) {
        const { type, message } = handleError(error, "Tarif-Gruppierung löschen");
        toastStore.add({ type, caption: "Fehler", message });
      }
    }
  };

  const handleUpdateTariffGrouping = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTariffGrouping) return;

    const confirmed = await showConfirmationModal({
      title: "Tarif-Gruppierung aktualisieren",
      text: "Möchten Sie die Änderungen speichern?",
      confirmText: "Speichern",
      cancelText: "Abbrechen",
    });

    if (confirmed) {
      try {
        await tariffGroupingStore.updateTariffGrouping(editingTariffGrouping.id, {
          ...editingTariffGrouping,
          terms: editingTariffGrouping.terms.filter((term) => term.trim()),
        });
        toastStore.add({
          type: "success",
          caption: "Erfolg",
          message: "Tarif-Gruppierung wurde erfolgreich aktualisiert",
        });
        setEditingTariffGrouping(null);
      } catch (error) {
        const { type, message } = handleError(error, "Tarif-Gruppierung aktualisieren");
        toastStore.add({ type, caption: "Fehler", message });
      }
    }
  };

  const getMenuActions = (tariffGrouping: TariffGroupingDTO): MenuAction[] => [
    {
      label: "Bearbeiten",
      icon: <PencilIcon className="size-4" />,
      onClick: () => setEditingTariffGrouping(tariffGrouping),
    },
    {
      label: "Löschen",
      icon: <TrashIcon className="size-4" />,
      onClick: () => handleDeleteTariffGrouping(tariffGrouping.id),
      variant: "error",
    },
  ];

  const renderTariffGroupingRow = (tariffGrouping: TariffGroupingDTO) => (
    <tr key={tariffGrouping.id} className="border-b border-neutral last:border-b-0">
      <td>
        <div className="flex-grow min-w-0 mr-2">
          <div className="text-sm font-bold truncate" title={tariffGrouping.name}>
            {tariffGrouping.name}
          </div>
          <div className="text-xs text-neutral-400">
            <div className="truncate">
              Begriffe: {tariffGrouping.terms.slice(0, 5).join(", ")}
              {tariffGrouping.terms.length > 5 && `, ... (+${tariffGrouping.terms.length - 5} weitere)`}
            </div>
          </div>
        </div>
      </td>
      <td>
        <TableMenu actions={getMenuActions(tariffGrouping)} />
      </td>
    </tr>
  );

  return (
    <SidebarPageLayout title="Tarif-Gruppierungen" onRefresh={() => tariffGroupingStore.refreshTariffGroupings()}>
      <ConfirmationModalComponent />
      <div className="flex gap-16">
        {/* Linke Seite - Tarif-Gruppierungs-Liste */}
        <div className="flex-1 border border-neutral rounded p-4">
          <div className="text-lg font-bold mb-4">Vorhandene Tarif-Gruppierungen</div>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="table w-full">
              <tbody>{tariffGroupingStore.tariffGroupings.map(renderTariffGroupingRow)}</tbody>
            </table>
          </div>
        </div>

        {/* Rechte Seite - Tarif-Gruppierung erstellen/bearbeiten */}
        <div className="flex-1 border border-neutral rounded p-4">
          <div className="text-lg font-bold mb-4">{editingTariffGrouping ? "Tarif-Gruppierung bearbeiten" : "Tarif-Gruppierung erstellen"}</div>
          <form onSubmit={editingTariffGrouping ? handleUpdateTariffGrouping : handleCreateTariffGrouping}>
            <div className="flex flex-col gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Name</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered"
                  value={editingTariffGrouping?.name ?? newTariffGrouping.name}
                  onChange={(e) => {
                    if (editingTariffGrouping) {
                      setEditingTariffGrouping({ ...editingTariffGrouping, name: e.target.value });
                    } else {
                      setNewTariffGrouping({ ...newTariffGrouping, name: e.target.value });
                    }
                  }}
                  placeholder="Name der Gruppierung"
                />
              </div>

              <div className="form-control">
                <span className="label-text-alt">Tarifnamen, die diese Begriffe enthalten, werden gruppiert</span>
                <div className="max-h-[40vh] overflow-y-auto mt-2">
                  {(editingTariffGrouping?.terms ?? newTariffGrouping.terms).map((term, index) => (
                    <div key={index} className="flex gap-2 items-center mb-2">
                      <span className="text-sm">Begriff:</span>
                      <input
                        type="text"
                        className="input input-bordered input-sm flex-1"
                        value={term}
                        onChange={(e) => handleTermChange(index, e.target.value)}
                        placeholder="Begriff (z.B. Black, Red, Online)"
                      />
                      {((editingTariffGrouping?.terms?.length ?? 0) > 1 || (!editingTariffGrouping && newTariffGrouping.terms.length > 1)) && (
                        <button type="button" className="btn btn-ghost btn-sm px-2" onClick={() => handleRemoveTerm(index)}>
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button type="button" className="btn btn-ghost" onClick={handleAddTerm}>
                  + Begriff hinzufügen
                </button>
                {editingTariffGrouping && (
                  <button type="button" className="btn btn-ghost" onClick={() => setEditingTariffGrouping(null)}>
                    Abbrechen
                  </button>
                )}
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    editingTariffGrouping
                      ? !editingTariffGrouping.name || editingTariffGrouping.terms.some((term) => !term.trim())
                      : !newTariffGrouping.name || newTariffGrouping.terms.some((term) => !term.trim())
                  }
                >
                  {editingTariffGrouping ? "Tarif-Gruppierung aktualisieren" : "Tarif-Gruppierung speichern"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </SidebarPageLayout>
  );
});

export default TariffGroupingPage;
